import { PipeAction } from "@/types/Workflow";
import {
  GUEST_UPDATE_ENUM_VALUES,
  GUEST_UPDATE_VALUE_REQUIRED,
} from "@/lib/utils/guestFieldWhitelist";

/**
 * How the `value` is applied to the guest field:
 *   - `"set"` (default / omitted): overwrite the field with `value`.
 *   - `"increment"`: add `value` to the current numeric value.
 *   - `"decrement"`: subtract `value` from the current numeric value.
 *
 * Increment/decrement only make sense for numeric fields (LONG/DOUBLE/CURRENCY);
 * the consumer executor treats a missing/`"set"` op as an overwrite.
 */
export type GuestUpdateOp = "set" | "increment" | "decrement";

/**
 * Where a row's value comes from when it is not typed in. `mainGuest` reads
 * `field` off the target guest's MAIN GUEST — the write-side twin of the
 * `{{mainGuest.<field>}}` mail placeholders.
 *
 * A target that HAS no main guest (it is one itself) makes the consumer SKIP
 * that assignment: the column keeps its value rather than being emptied. See
 * `main-consumer` `spi/guest-update-gateway.ts`.
 */
export type GuestUpdateSource =
  | { scope: "mainGuest"; field: string }
  /**
   * `expr` — COMPUTE the value from the target guest's own number fields,
   * with the same arithmetic tree a computed field and a companion allowance
   * carry. The gateway reads only the cells the tree names and skips the
   * assignment when the guest has no answer, so a pipe never writes the `0`
   * that an unreadable value would otherwise become.
   */
  | { scope: "expr"; expr: unknown };

/** One field assignment inside an UPDATE_GUEST action. */
export type GuestUpdate = {
  field: string;
  value: unknown;
  op?: GuestUpdateOp;
  /** Present ⇒ `value` is ignored and the value is read from the source row. */
  copyFrom?: GuestUpdateSource;
  /**
   * EDITOR-ONLY: the value box shows text `value` does not hold (an
   * unstorable email — the EMAIL cell keeps the last storable address in
   * `value`). Refuses the save doors like any other row issue and is stripped
   * by `normalizeGuestUpdates` before the config goes to the wire.
   */
  valueInvalid?: true;
};

/**
 * Read a value source out of one raw row. Anything incomplete or unknown is
 * dropped, and the row falls back to its literal — the same forgiving read
 * `op` gets, and the same one the consumer's twin performs.
 */
export function readGuestUpdateSource(raw: unknown): GuestUpdateSource | undefined {
  if (raw === null || typeof raw !== "object") return undefined;
  const scope = (raw as { scope?: unknown }).scope;
  const field = (raw as { field?: unknown }).field;
  if (scope === "expr") {
    // The tree rides unparsed: `GuestUpdateExprValidator` on the workflow write
    // door judges it against the event's fields (operand resolves, is not
    // itself computed, is not IT-04 protected; target is writable and numeric)
    // and the consumer's gateway refuses one it cannot evaluate.
    //
    // That validator is NEW. This comment previously claimed the backend
    // validated the shape while nothing on the JVM looked inside an action
    // config at all — which is how `gehalt * 1` could copy an ART9 value into
    // an unrestricted column. A row whose `expr` is
    // still missing is KEPT (as `undefined`) for the same reason a
    // source-with-no-field is: the save guard can only refuse a decision it
    // can see.
    return { scope, expr: (raw as { expr?: unknown }).expr };
  }
  if (scope !== "mainGuest") return undefined;
  // A KNOWN scope with no field yet is kept, deliberately, as `field: ""`.
  //
  // Dropping it — which this did — is what made the save-time guard dead code:
  // the validators ask `row.copyFrom`, so a source the reader had already
  // thrown away could never be reported. The row then saved as a bare empty
  // literal, and an empty literal is written as an `$unset`: the automation
  // CLEARED the column it was asked to fill, on every guest it reached, with
  // no history row and nothing in the log. Keeping the half-made decision
  // visible is the only way the doors can refuse it.
  return { scope, field: typeof field === "string" ? field : "" };
}

/**
 * True when a row is an increment/decrement with no usable amount. The executor
 * rejects this at run time (`op requires a numeric amount`), so the form blocks
 * it at save. A plain "set" with an empty value is a legitimate clear → false.
 */
export function isEmptyDeltaAmount(
  op: GuestUpdateOp | undefined,
  value: unknown,
  copyFrom?: GuestUpdateSource,
): boolean {
  if (copyFrom) return false;
  if (op !== "increment" && op !== "decrement") return false;
  return (
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim().length === 0)
  );
}

/** Why an UPDATE_GUEST row's value is unusable, or null when it is fine. */
export type GuestUpdateValueIssue = "required" | "invalid";

/**
 * FW12 — validate ONE row's value against what the executor will accept.
 *
 * Sibling of {@link isEmptyDeltaAmount} and it exists for the same reason: the
 * executor rejects these at run time, so a pipe carrying one saves green and
 * then fails on every run, visible only in the workflow log.
 *
 * Two rules, both mirroring the consumer exactly:
 *   - a field in `GUEST_UPDATE_VALUE_REQUIRED` (only `state`) cannot be
 *     cleared — every other column treats empty as a legitimate `$unset`;
 *   - a field in `GUEST_UPDATE_ENUM_VALUES` must carry one of its values.
 *     Case is folded first because the gateway upper-cases before writing, so
 *     rejecting `"accepted"` here would block a config the runtime applies.
 *
 * Custom (additionalFields) rows are NOT checked here — the executor resolves
 * those against the event's own schema and validates their options there.
 */
export function guestUpdateValueIssue(
  field: string,
  value: unknown,
  copyFrom?: GuestUpdateSource,
): GuestUpdateValueIssue | null {
  // A sourced row has no literal to judge: its value is read at run time off
  // the main guest. Judging the (empty) literal here would report "value
  // required" on a row that carries a perfectly good source.
  if (copyFrom) return null;
  const isEmpty =
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim().length === 0) ||
    (Array.isArray(value) && value.length === 0);

  if (isEmpty) {
    return GUEST_UPDATE_VALUE_REQUIRED.has(field) ? "required" : null;
  }

  const allowed = GUEST_UPDATE_ENUM_VALUES.get(field);
  if (!allowed) return null;
  const name = typeof value === "string" ? value.trim().toUpperCase() : "";
  return allowed.includes(name) ? null : "invalid";
}

/** Why one UPDATE_GUEST row cannot be saved, or null when it is fine. */
export type GuestUpdateRowIssue =
  /** No target field picked. */
  | "field"
  /** "Take it from the main guest" with no source field picked. */
  | "source"
  /** Increment/decrement with no amount. */
  | "amount"
  /** A copy that also carries a delta op — a copy is a SET. */
  | "op"
  | "valueRequired"
  | "valueInvalid";

/**
 * The ONE authority on whether a row may be saved, for all three doors that ask
 * (`useWorkflowForm.validate` and the wizard's two gates).
 *
 * They used to ask three slightly different questions, and the one that
 * mattered — a source with no field — was asked in a way that could never fire.
 * A row that is refused here is a row the user can still fix; every case below
 * is one the runtime would otherwise turn into a silent write of the wrong
 * thing.
 */
export function guestUpdateRowIssue(row: GuestUpdate): GuestUpdateRowIssue | null {
  if (typeof row.field !== "string" || row.field.trim().length === 0) return "field";
  if (row.copyFrom) {
    if (row.copyFrom.scope === "expr") {
      // The same two questions as a copy, asked of a calculation: is there one
      // at all, and is it being ADDED to the target instead of set.
      if (row.copyFrom.expr === undefined || row.copyFrom.expr === null) return "source";
      if (row.op === "increment" || row.op === "decrement") return "op";
      return null;
    }
    if (row.copyFrom.field.length === 0) return "source";
    // Hidden foot-gun: the editor hides the mode control once a source is
    // picked, so an `op` left over from before the switch would `$inc` by the
    // main guest's value on every run — a counter that grows forever, with no
    // control on screen to see or undo it.
    if (row.op === "increment" || row.op === "decrement") return "op";
    return null;
  }
  if (isEmptyDeltaAmount(row.op, row.value)) return "amount";
  // The box disagrees with the literal (editor-only marker, see GuestUpdate).
  if (row.valueInvalid) return "valueInvalid";
  const valueIssue = guestUpdateValueIssue(row.field, row.value);
  if (valueIssue === "required") return "valueRequired";
  if (valueIssue === "invalid") return "valueInvalid";
  return null;
}

/** Why a whole UPDATE_GUEST action cannot be saved, or null when it is fine. */
export type GuestUpdateActionIssue = "empty" | GuestUpdateRowIssue;

/**
 * The whole question a save door asks about ONE UPDATE_GUEST action.
 *
 * Exists so the three doors (`useWorkflowForm.validate` and the wizard's step
 * gate and inline error) are one line each and share one tested decision. They
 * were three hand-written variants; the one that mattered — a value source with
 * no field — was written in a shape that could never fire, and the wizard pair
 * never learned about it at all.
 */
export function guestUpdateActionIssue(
  config: Record<string, unknown>,
): GuestUpdateActionIssue | null {
  const rows = readGuestUpdates(config);
  if (rows.length === 0) return "empty";
  return rows.map(guestUpdateRowIssue).find((x) => x !== null) ?? null;
}

/**
 * Read the field assignments out of an UPDATE_GUEST action config, accepting
 * both shapes:
 *   - canonical: `config.updates: [{field, value}]`
 *   - legacy single-field: `config.field` / `config.value`
 *
 * Rows without a non-empty string `field` are dropped. The consumer executor
 * applies the same precedence, so this stays the single source of truth on the
 * FE for display, validation and save-time normalization.
 */
export function readGuestUpdates(config: Record<string, unknown>): GuestUpdate[] {
  const raw = config["updates"];
  if (Array.isArray(raw)) {
    return raw
      .filter(
        (u): u is { field: string; value?: unknown } =>
          typeof u === "object" &&
          u !== null &&
          typeof (u as { field?: unknown }).field === "string" &&
          ((u as { field: string }).field).length > 0,
      )
      .map((u) => {
        const op = (u as { op?: unknown }).op;
        const copyFrom = readGuestUpdateSource((u as { copyFrom?: unknown }).copyFrom);
        // Only carry a non-default op; a missing/"set" op means overwrite.
        const base: GuestUpdate =
          op === "increment" || op === "decrement"
            ? { field: u.field, value: u.value ?? "", op }
            : { field: u.field, value: u.value ?? "" };
        const withSource = copyFrom ? { ...base, copyFrom } : base;
        return (u as { valueInvalid?: unknown }).valueInvalid === true
          ? { ...withSource, valueInvalid: true as const }
          : withSource;
      });
  }
  const field = config["field"];
  if (typeof field === "string" && field.length > 0) {
    const copyFrom = readGuestUpdateSource(config["copyFrom"]);
    const row: GuestUpdate = { field, value: config["value"] ?? "" };
    return [copyFrom ? { ...row, copyFrom } : row];
  }
  return [];
}

/**
 * Save-time normalization for an UPDATE_GUEST action: collapse it to the
 * canonical `config.updates` array (dropping empty rows and the legacy
 * `field`/`value` keys). Call only for UPDATE_GUEST actions.
 */
export function normalizeGuestUpdates(action: PipeAction): PipeAction {
  // A row whose source is half-made is DROPPED, not flattened to its literal.
  // Dropping writes nothing; flattening writes `""`, which the consumer applies
  // as an `$unset` — the one outcome this feature must never have. The save
  // doors refuse such a row anyway (`guestUpdateRowIssue`), so this is the belt
  // for a config that arrives from anywhere else: an older editor, the AI
  // authoring tool, a hand-edited pipe.
  const updates = readGuestUpdates(action.config)
    .filter(
      (u) =>
        !u.copyFrom ||
        (u.copyFrom.scope === "expr"
          ? u.copyFrom.expr !== undefined && u.copyFrom.expr !== null
          : u.copyFrom.field.length > 0),
    )
    // The editor-only marker never travels: the doors refused the save while
    // it was set, and a config carrying it would be a shape the runtime does
    // not know.
    .map(({ valueInvalid: _vi, ...rest }) => rest);
  const { field: _f, value: _v, copyFrom: _c, updates: _u, ...rest } = action.config;
  return { ...action, config: { ...rest, updates } };
}
